import { TrackedSession } from '../types/common.types';
import {
  AccountabilityTemplate,
  AttendanceSession,
  AttendanceSessionTemplate,
  CharityActivity,
  CheckpointTemplate,
  Condition,
  Measurement,
  OptionalExercise,
  ParentProgrammeGeneralDetails,
  Programme,
  RecommendedExercise,
  RegistrationRequirement,
  SelfCareActivity,
  SubProgrammeGeneralDetails,
  SupportTemplate,
  TribeTemplate,
  Weighting
} from '../types/entity.types';

import {
  initialCharityActivities,
  initialConditions,
  initialOptionalExercises,
  initialRecommendedExercises,
  initialSelfCareActivities,
  mapTrackedSessionsToTrackedSessionTemplates
} from './accountability.util';
import { convertDateToTimeString } from './util';

import { UPCOMING } from './constants';

export const checkForGeneralChanges = (
  oldProgramme: Programme | undefined,
  newProgramme: Programme,
  parentProgramme: ParentProgrammeGeneralDetails | undefined,
  subProgramme: SubProgrammeGeneralDetails | undefined
) => {
  if (oldProgramme?.status === UPCOMING && !oldProgramme.externalReference) {
    return checkForNewProgrammeChanges(newProgramme) ? ['new'] : [];
  }

  if (parentProgramme && subProgramme) {
    return checkForProgrammeOfProgrammeChanges(newProgramme, parentProgramme, subProgramme);
  }

  return checkForNormalProgrammeChanges(oldProgramme, newProgramme) ? ['normal'] : [];
};

const checkForNewProgrammeChanges = (programme: Programme) => {
  if (programme.name) return true;
  if (programme.description) return true;
  if (programme.toDate) return true;
  if (programme.fromDate) return true;
  if (!programme.isNutrition) return true;
  if (!programme.isMovement) return true;
  if (!programme.isMeasurements) return true;
  if (!programme.isSleep) return true;
  if (!programme.isCondition) return true;
  if (!programme.isSelfCare) return true;
  if (!programme.isSession) return true;
  if (!programme.charityWork) return true;
  if (programme.leaderboardCollectFrom) return true;
  if (programme.hasRegistrationRequirements) return true;
  if (programme.subProgrammes?.filter((sub) => !!sub.name).length) return true;
  if (programme.theme) return true;

  return false;
};

const checkForProgrammeOfProgrammeChanges = (
  programme: Programme,
  parentProgramme: ParentProgrammeGeneralDetails,
  subProgramme: SubProgrammeGeneralDetails
) => {
  const changes = [];

  if (
    programme.name !== parentProgramme.programmeGroupName ||
    programme.fromDate?.toJSON() !== parentProgramme.runningFromDate ||
    programme.toDate?.toJSON() !== parentProgramme.runningToDate
  ) {
    changes.push('parent');
  }

  if (
    programme.subProgrammeName !== subProgramme.name ||
    programme.theme !== subProgramme.theme ||
    programme.description !== subProgramme.description ||
    programme.isNutrition !== subProgramme.nutritionEnabled ||
    programme.isMovement !== subProgramme.movementEnabled ||
    programme.isMeasurements !== subProgramme.measurementEnabled ||
    programme.isSleep !== subProgramme.sleepEnabled ||
    programme.isCondition !== subProgramme.conditionEnabled ||
    programme.isSelfCare !== subProgramme.selfCareEnabled ||
    programme.isSession !== subProgramme.sessionEnabled ||
    programme.charityWork !== subProgramme.charityWorkEnabled ||
    programme.leaderboardCollectFrom?.toJSON() !== subProgramme.leaderboardCollectFrom ||
    checkForRegistrationRequirementChanges(programme.registrationRequirement, subProgramme)
  ) {
    changes.push('sub');
  }

  return changes;
};

const checkForNormalProgrammeChanges = (oldProgramme: Programme | undefined, newProgramme: Programme) => {
  if (oldProgramme?.name !== newProgramme.name) return true;
  if (oldProgramme.description !== newProgramme.description) return true;
  if (oldProgramme.toDate !== newProgramme.toDate?.toJSON()) return true;
  if (oldProgramme.fromDate !== newProgramme.fromDate?.toJSON()) return true;
  if (oldProgramme.isNutrition !== newProgramme.isNutrition) return true;
  if (oldProgramme.isMovement !== newProgramme.isMovement) return true;
  if (oldProgramme.isMeasurements !== newProgramme.isMeasurements) return true;
  if (oldProgramme.isSleep !== newProgramme.isSleep) return true;
  if (oldProgramme.isCondition !== newProgramme.isCondition) return true;
  if (oldProgramme.isSelfCare !== newProgramme.isSelfCare) return true;
  if (oldProgramme.isSession !== newProgramme.isSession) return true;
  if (oldProgramme.charityWork !== newProgramme.charityWork) return true;
  if (oldProgramme.leaderboardCollectFrom !== newProgramme.leaderboardCollectFrom?.toJSON()) return true;
  if (oldProgramme.hasRegistrationRequirements !== newProgramme.hasRegistrationRequirements) return true;

  return checkForRegistrationRequirementChanges(
    oldProgramme.registrationRequirement,
    newProgramme.registrationRequirement
  );
};

const checkForRegistrationRequirementChanges = (
  oldRegistrationRequirement: RegistrationRequirement | SubProgrammeGeneralDetails | null | undefined,
  newRegistrationRequirement: RegistrationRequirement | SubProgrammeGeneralDetails | null | undefined
) => {
  if (oldRegistrationRequirement && newRegistrationRequirement) {
    if (
      !!oldRegistrationRequirement.payfastMerchantId !== !!newRegistrationRequirement.payfastMerchantId &&
      oldRegistrationRequirement.payfastMerchantId !== newRegistrationRequirement.payfastMerchantId
    ) {
      return true;
    }

    if (
      !!oldRegistrationRequirement.payfastMerchantKey !== !!newRegistrationRequirement.payfastMerchantKey &&
      oldRegistrationRequirement.payfastMerchantKey !== newRegistrationRequirement.payfastMerchantKey
    ) {
      return true;
    }

    if (oldRegistrationRequirement.programmePrice !== newRegistrationRequirement.programmePrice) return true;

    const oldRequiredFields = oldRegistrationRequirement.requiredRegistrationFields;
    const newRequiredFields = newRegistrationRequirement.requiredRegistrationFields;

    if (oldRequiredFields && newRequiredFields) {
      if (oldRequiredFields.length !== newRequiredFields.length) return true;
      for (let i = 0; i < oldRequiredFields.length; i++) {
        if (oldRequiredFields[i] !== newRequiredFields[i]) return true;
      }
    }
  }

  return false;
};

export const checkForAccountabilityChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  weightings: Weighting,
  measurement: Measurement,
  conditions: Condition[],
  recommendedExercises: RecommendedExercise[],
  optionalExercises: OptionalExercise[],
  selfCareActivities: SelfCareActivity[],
  sessions: AttendanceSession[],
  trackedSessions: TrackedSession[],
  charityTemplates: CharityActivity[]
) => {
  let isChanged = checkForWeightingChanges(accountabilityTemplate, weightings);
  if (!isChanged) isChanged = checkForMeasurementChanges(accountabilityTemplate, measurement);
  if (!isChanged) isChanged = checkForConditionChanges(accountabilityTemplate, conditions);
  if (!isChanged) isChanged = checkForRecommendedExercisesChanges(accountabilityTemplate, recommendedExercises);
  if (!isChanged) isChanged = checkForOptionalExercisesChanges(accountabilityTemplate, optionalExercises);
  if (!isChanged) isChanged = checkForSelfCareActivitiesChanges(accountabilityTemplate, selfCareActivities);
  if (!isChanged) isChanged = checkForAttendanceSessionChanges(accountabilityTemplate, sessions);
  if (!isChanged) isChanged = checkForTrackedSessionChanges(accountabilityTemplate, trackedSessions);
  if (!isChanged) isChanged = checkForCharityChanges(accountabilityTemplate, charityTemplates);

  return isChanged;
};

export const checkForSupportChanges = (
  oldSupport: SupportTemplate | null | undefined,
  newSupport: SupportTemplate | null | undefined
) => {
  if (!oldSupport && newSupport) {
    if (newSupport.firstName) return true;
    if (newSupport.lastName) return true;
    if (newSupport.email) return true;
    if (newSupport.phone) return true;
    if (newSupport.frequentlyAskedQuestionsString) return true;
  }

  if (oldSupport && newSupport) {
    if (oldSupport.firstName !== newSupport.firstName) return true;
    if (oldSupport.lastName !== newSupport.lastName) return true;
    if (oldSupport.email !== newSupport.email) return true;
    if (oldSupport.phone !== newSupport.phone) return true;
    if (oldSupport.frequentlyAskedQuestionsString !== newSupport.frequentlyAskedQuestionsString) return true;
  }

  return false;
};

export const checkForTribeChanges = (
  oldTribe: TribeTemplate | null | undefined,
  newTribe: TribeTemplate | null | undefined
) => {
  if (!oldTribe && newTribe) {
    if (newTribe.approvePosts) return true;
    if (newTribe.approvePhotos) return true;
    if (newTribe.approveComments) return true;
    if (newTribe.hashTags && newTribe.hashTags.length) return true;
  }

  if (oldTribe && newTribe) {
    if (oldTribe.approvePosts !== newTribe.approvePosts) return true;
    if (oldTribe.approvePhotos !== newTribe.approvePhotos) return true;
    if (oldTribe.approveComments !== newTribe.approveComments) return true;
    if (oldTribe.hashTags!.length !== newTribe.hashTags!.length) return true;

    for (let i = 0; i < oldTribe.hashTags!.length; i++) {
      if (oldTribe.hashTags![i]!.text !== newTribe.hashTags![i]!.text) return true;
    }
  }

  return false;
};

const checkForWeightingChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  weightings: Weighting
) => {
  if (!accountabilityTemplate) {
    if (weightings.weight) return true;
    if (weightings.cm) return true;
    if (weightings.steps) return true;
    if (weightings.objectives) return true;

    return false;
  }

  const measurementTemplate = accountabilityTemplate.measurementTemplate!;
  if (measurementTemplate.weight !== weightings.weight) return true;
  if (measurementTemplate.cm !== weightings.cm) return true;
  if (measurementTemplate.steps !== weightings.steps) return true;
  if (measurementTemplate.objectives !== weightings.objectives) return true;

  return false;
};

const checkForMeasurementChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  measurement: Measurement
) => {
  if (!accountabilityTemplate) {
    if (measurement.measurementFrequency) return true;
    if (measurement.measurementStartDay) return true;
    if (measurement.measurementDiagram) return true;

    return false;
  }

  const templateMeasurement = accountabilityTemplate.measurement!;
  if (templateMeasurement.measurementFrequency !== measurement.measurementFrequency) return true;
  if (templateMeasurement.measurementStartDay !== measurement.measurementStartDay) return true;
  if (templateMeasurement.measurementDiagram !== measurement.measurementDiagram) return true;

  return false;
};

const checkForConditionChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  conditions: Condition[]
) => {
  if (!accountabilityTemplate) {
    if (conditions.length !== initialConditions.length) return true;

    for (let i = 0; i < conditions.length; i++) {
      const condition = conditions[i]!;
      const initialCondition = initialConditions[i]!;

      if (condition.optionOne !== initialCondition.optionOne) return true;
      if (condition.optionTwo !== initialCondition.optionTwo) return true;
    }

    return false;
  }

  const templateConditions = accountabilityTemplate.conditions!;
  if (templateConditions.length !== conditions.length) return true;

  for (let i = 0; i < conditions.length; i++) {
    const templateCondition = templateConditions[i]!;
    const condition = conditions[i]!;

    if (templateCondition.optionOne !== condition.optionOne) return true;
    if (templateCondition.optionTwo !== condition.optionTwo) return true;
  }

  return false;
};

const checkForRecommendedExercisesChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  recommendedExercises: RecommendedExercise[]
) => {
  if (!accountabilityTemplate) {
    if (recommendedExercises.length !== initialRecommendedExercises.length) return true;

    for (let i = 0; i < recommendedExercises.length; i++) {
      const recommendedExercise = recommendedExercises[i]!;
      const initialRecommendedExercise = initialRecommendedExercises[i]!;

      if (recommendedExercise.externalReference !== initialRecommendedExercise.externalReference) return true;
      if (recommendedExercise.name !== initialRecommendedExercise.name) return true;
      if (recommendedExercise.measurement !== initialRecommendedExercise.measurement) return true;

      const amounts = recommendedExercise.amounts;
      const initialAmounts = initialRecommendedExercise.amounts;
      if (amounts.length !== initialAmounts.length) return true;

      for (let j = 0; j < amounts.length; j++) {
        const amount = amounts[j]!;
        const initialAmount = initialAmounts[j]!;

        if (amount.externalReference !== initialAmount.externalReference) return true;
        if (amount.fromWeek !== initialAmount.fromWeek) return true;
        if (amount.toWeek !== initialAmount.toWeek) return true;
        if (amount.amount !== initialAmount.amount) return true;
      }
    }

    return false;
  }

  const mappedRecommendedExercises: Array<{
    externalReference: string;
    name: string;
    dailyAmount: string;
    measurement: string;
    fromWeek: string;
    toWeek: string;
  }> = [];

  recommendedExercises.forEach((exercise) => {
    exercise.amounts.forEach((amount) => {
      if (!exercise.name && !exercise.measurement && !amount.amount && !amount.fromWeek && !amount.toWeek) return;

      mappedRecommendedExercises.push({
        externalReference: amount.externalReference,
        name: exercise.name,
        dailyAmount: amount.amount,
        measurement: exercise.measurement,
        fromWeek: amount.fromWeek,
        toWeek: amount.toWeek
      });
    });
  });

  const templateRecommendedExercises = accountabilityTemplate.recommendedExercise!;
  if (templateRecommendedExercises.length !== mappedRecommendedExercises.length) return true;

  for (let i = 0; i < templateRecommendedExercises.length; i++) {
    const templateRecommendedExercise = templateRecommendedExercises[i]!;
    const mappedRecommendedExercise = mappedRecommendedExercises[i]!;

    if (templateRecommendedExercise.externalReference !== mappedRecommendedExercise.externalReference) return true;
    if (templateRecommendedExercise.name !== mappedRecommendedExercise.name) return true;
    if (templateRecommendedExercise.dailyAmount !== mappedRecommendedExercise.dailyAmount) return true;
    if (templateRecommendedExercise.measurement !== mappedRecommendedExercise.measurement) return true;
    if (templateRecommendedExercise.fromWeek !== mappedRecommendedExercise.fromWeek) return true;
    if (templateRecommendedExercise.toWeek !== mappedRecommendedExercise.toWeek) return true;
  }

  return false;
};

const checkForOptionalExercisesChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  optionalExercises: OptionalExercise[]
) => {
  if (!accountabilityTemplate) {
    if (optionalExercises.length !== initialOptionalExercises.length) return true;

    for (let i = 0; i < optionalExercises.length; i++) {
      const optionalExercise = optionalExercises[i]!;
      const initialOptionalExercise = initialOptionalExercises[i]!;

      if (optionalExercise.externalReference !== initialOptionalExercise.externalReference) return true;
      if (optionalExercise.name !== initialOptionalExercise.name) return true;
      if (optionalExercise.measurement !== initialOptionalExercise.measurement) return true;
    }

    return false;
  }

  const templateOptionalExercises = accountabilityTemplate.optionalExercise!;
  if (templateOptionalExercises.length !== optionalExercises.length) {
    if (optionalExercises.length === 1 && templateOptionalExercises.length === 0) {
      const optionalExercise = optionalExercises[0]!;
      if (optionalExercise.name !== '') return true;
      if (optionalExercise.measurement !== '') return true;
    } else {
      return true;
    }
  } else {
    for (let i = 0; i < optionalExercises.length; i++) {
      const templateOptionalExercise = templateOptionalExercises[i]!;
      const optionalExercise = optionalExercises[i]!;

      if (templateOptionalExercise.externalReference !== optionalExercise.externalReference) return true;
      if (templateOptionalExercise.name !== optionalExercise.name) return true;
      if (templateOptionalExercise.measurement !== optionalExercise.measurement) return true;
    }
  }

  return false;
};

const checkForSelfCareActivitiesChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  selfCareActivities: SelfCareActivity[]
) => {
  if (!accountabilityTemplate) {
    if (selfCareActivities.length !== initialSelfCareActivities.length) return true;

    for (let i = 0; i < selfCareActivities.length; i++) {
      const selfCareActivity = selfCareActivities[i]!;
      const initialSelfCareActivity = initialSelfCareActivities[i]!;

      if (selfCareActivity.externalReference !== initialSelfCareActivity.externalReference) return true;
      if (selfCareActivity.name !== initialSelfCareActivity.name) return true;
    }

    return false;
  }

  const templateSelfCareActivities = accountabilityTemplate.selfCare!;
  if (templateSelfCareActivities.length !== selfCareActivities.length) {
    if (selfCareActivities.length === 1 && templateSelfCareActivities.length === 0) {
      if (selfCareActivities[0]!.name !== '') return true;
    } else {
      return true;
    }
  } else {
    for (let i = 0; i < selfCareActivities.length; i++) {
      const templateSelfCareActivity = templateSelfCareActivities[i]!;
      const selfCareActivity = selfCareActivities[i]!;

      if (templateSelfCareActivity.externalReference !== selfCareActivity.externalReference) return true;
      if (templateSelfCareActivity.name !== selfCareActivity.name) return true;
    }
  }

  return false;
};

const checkForAttendanceSessionChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  sessions: AttendanceSession[] | AttendanceSessionTemplate[]
) => {
  if (!accountabilityTemplate) {
    return sessions.length > 0;
  }

  const templateSessions = accountabilityTemplate.sessions;
  if (templateSessions!.length !== sessions.length) return true;

  for (let i = 0; i < sessions.length; i++) {
    const templateSession = templateSessions![i]!;
    const session = sessions[i]!;

    if (templateSession.externalReference !== session.externalReference) return true;
    if (templateSession.name !== session.name) return true;
    if (templateSession.startDate !== session.startDate) return true;
    if (templateSession.startTime !== session.startTime) return true;
    if (templateSession.endDate !== session.endDate) return true;
    if (templateSession.endTime !== session.endTime) return true;
    if (templateSession.reoccurrencePattern !== session.reoccurrencePattern) return true;
  }

  return false;
};

const isSameInstant = (oldValue: string | undefined, newValue: string | undefined) => {
  if (!oldValue && !newValue) return true;
  if (!oldValue || !newValue) return false;

  return new Date(oldValue).getTime() === new Date(newValue).getTime();
};

const isSameTimeOfDay = (oldValue: string | undefined, newValue: string | undefined) => {
  if (!oldValue && !newValue) return true;
  if (!oldValue || !newValue) return false;

  return convertDateToTimeString(oldValue) === convertDateToTimeString(newValue);
};

const checkForTrackedSessionChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  trackedSessions: TrackedSession[]
) => {
  if (!accountabilityTemplate) {
    return trackedSessions.length > 0;
  }

  const templateTrackedSessions = accountabilityTemplate.trackedSessionTemplates ?? [];
  const mappedTrackedSessions = mapTrackedSessionsToTrackedSessionTemplates(trackedSessions);

  if (templateTrackedSessions.length !== mappedTrackedSessions.length) return true;

  for (let i = 0; i < mappedTrackedSessions.length; i++) {
    const templateTrackedSession = templateTrackedSessions[i]!;
    const mappedTrackedSession = mappedTrackedSessions[i]!;

    if (templateTrackedSession.externalReference !== mappedTrackedSession.externalReference) return true;
    if (templateTrackedSession.name !== mappedTrackedSession.name) return true;
    if (!isSameInstant(templateTrackedSession.startDate, mappedTrackedSession.startDate)) return true;
    if (!isSameInstant(templateTrackedSession.startTime, mappedTrackedSession.startTime)) return true;
    if (!isSameInstant(templateTrackedSession.endTime, mappedTrackedSession.endTime)) return true;

    if (
      checkForCheckpointChanges(templateTrackedSession.checkpointTemplates, mappedTrackedSession.checkpointTemplates)
    ) {
      return true;
    }
  }

  return false;
};

const checkForCheckpointChanges = (
  templateCheckpoints: CheckpointTemplate[] | undefined,
  mappedCheckpoints: CheckpointTemplate[] | undefined
) => {
  const oldCheckpoints = templateCheckpoints ?? [];
  const newCheckpoints = mappedCheckpoints ?? [];

  if (oldCheckpoints.length !== newCheckpoints.length) return true;

  for (let i = 0; i < newCheckpoints.length; i++) {
    const oldCheckpoint = oldCheckpoints[i]!;
    const newCheckpoint = newCheckpoints[i]!;

    if ((oldCheckpoint.externalReference ?? undefined) !== (newCheckpoint.externalReference ?? undefined)) return true;
    if (oldCheckpoint.name !== newCheckpoint.name) return true;
    if (oldCheckpoint.deviceId !== newCheckpoint.deviceId) return true;
    if ((oldCheckpoint.index ?? 0) !== (newCheckpoint.index ?? 0)) return true;
    if (!!oldCheckpoint.manualStartTime !== !!newCheckpoint.manualStartTime) return true;
    if ((oldCheckpoint.basePoints ?? 0) !== (newCheckpoint.basePoints ?? 0)) return true;
    if ((oldCheckpoint.targetTimeInMinutes ?? 0) !== (newCheckpoint.targetTimeInMinutes ?? 0)) return true;
    if ((oldCheckpoint.bonusPoints ?? 0) !== (newCheckpoint.bonusPoints ?? 0)) return true;
    if (!!oldCheckpoint.multiplePassesConfigured !== !!newCheckpoint.multiplePassesConfigured) return true;
    if ((oldCheckpoint.multiplePassBasePoints ?? 0) !== (newCheckpoint.multiplePassBasePoints ?? 0)) return true;
    if ((oldCheckpoint.multiplePassCompletedBasePoints ?? 0) !== (newCheckpoint.multiplePassCompletedBasePoints ?? 0)) {
      return true;
    }
    if ((oldCheckpoint.multiplePassTargetTimeInMinutes ?? 0) !== (newCheckpoint.multiplePassTargetTimeInMinutes ?? 0)) {
      return true;
    }
    if ((oldCheckpoint.multiplePassBonusPoints ?? 0) !== (newCheckpoint.multiplePassBonusPoints ?? 0)) return true;
    if (!isSameTimeOfDay(oldCheckpoint.startTime ?? undefined, newCheckpoint.startTime ?? undefined)) return true;

    const oldPasses = oldCheckpoint.checkpointPasses ?? [];
    const newPasses = newCheckpoint.checkpointPasses ?? [];
    if (oldPasses.length !== newPasses.length) return true;

    for (let j = 0; j < newPasses.length; j++) {
      if (oldPasses[j]!.index !== newPasses[j]!.index) return true;
      if (oldPasses[j]!.name !== newPasses[j]!.name) return true;
    }
  }

  return false;
};

const checkForCharityChanges = (
  accountabilityTemplate: AccountabilityTemplate | null | undefined,
  charityActivities: CharityActivity[]
) => {
  if (!accountabilityTemplate) {
    if (charityActivities.length !== initialCharityActivities.length) return true;

    for (let i = 0; i < charityActivities.length; i++) {
      const charityActivity = charityActivities[i]!;
      const initialCharityActivity = initialCharityActivities[i]!;

      if (charityActivity.externalReference !== initialCharityActivity.externalReference) return true;
      if (charityActivity.name !== initialCharityActivity.name) return true;
      if (charityActivity.measurement !== initialCharityActivity.measurement) return true;

      const amounts = charityActivity.amounts;
      const initialAmounts = initialCharityActivity.amounts;
      if (amounts.length !== initialAmounts.length) return true;

      for (let j = 0; j < amounts.length; j++) {
        const amount = amounts[j]!;
        const initialAmount = initialAmounts[j]!;

        if (amount.externalReference !== initialAmount.externalReference) return true;
        if (amount.fromWeek !== initialAmount.fromWeek) return true;
        if (amount.toWeek !== initialAmount.toWeek) return true;
        if (amount.amount !== initialAmount.amount) return true;
      }
    }

    return false;
  }

  const filteredCharityActivities = charityActivities.filter(({ name, measurement, amounts }) => {
    const isFilledIn = name || measurement;

    if (isFilledIn) return true;

    for (const { amount, fromWeek, toWeek } of amounts) {
      if (amount || fromWeek || toWeek) {
        return true;
      }
    }

    return false;
  });

  const mappedCharityActivities: Array<{
    externalReference: string;
    name: string;
    dailyAmount: string;
    measurement: string;
    fromWeek: string;
    toWeek: string;
  }> = [];

  filteredCharityActivities.forEach((charityActivity) => {
    charityActivity.amounts.forEach((amount) => {
      mappedCharityActivities.push({
        externalReference: amount.externalReference,
        name: charityActivity.name,
        dailyAmount: amount.amount,
        measurement: charityActivity.measurement,
        fromWeek: amount.fromWeek,
        toWeek: amount.toWeek
      });
    });
  });

  const { charityTemplates } = accountabilityTemplate;
  if (charityTemplates!.length !== mappedCharityActivities.length) return true;

  for (let i = 0; i < charityTemplates!.length; i++) {
    const charityTemplate = charityTemplates![i]!;
    const mappedCharityActivity = mappedCharityActivities[i]!;

    if (charityTemplate.externalReference !== mappedCharityActivity.externalReference) return true;
    if (charityTemplate.name !== mappedCharityActivity.name) return true;
    if (charityTemplate.dailyAmount !== mappedCharityActivity.dailyAmount) return true;
    if (charityTemplate.measurement !== mappedCharityActivity.measurement) return true;
    if (charityTemplate.fromWeek !== mappedCharityActivity.fromWeek) return true;
    if (charityTemplate.toWeek !== mappedCharityActivity.toWeek) return true;
  }

  return false;
};
